import { AxiosCalls } from "../Logic/Functions/AxiosCalls";

import { handleDefaultApiErrors } from "Utils/Global/apiErrorHandling";
import type {
  CategoriesMeta,
  CategoriesResponse,
  GetCategoryBySlugResponse,
  ICategoriesData,
  ICategoryView,
  IHandleInvalidEditCategoryFormParams,
  IHandleCancelEditCategoryFormParams,
  IHandleCreateCategoryParams,
  IHandleDeleteCategoryParams,
  IHandleEditCategoryParams,
  IHandleGetCategoriesParams,
  IHandleGetCategoryBySlugParams,
  IResetEditCategoryFormParams,
  LoadedCategory,
  IHandleGetAdminCategoriesParams,
  IHandleGetCategoryBySlugAdminParams
} from "@model/Category.model";
import {
  ICategoryData,
  IHandleGetCategoriesWithProductsParams,
} from "@model/Products.model";
import { ExistingUploadFile } from "@model/UIComponents.model";

export class CategoriesService {
  async handleGetAllCategories({
    page,
    perPage,
    setLoading,
    setApiError,
    setItems,
    setTotal,
    setPage,
    setPerPage,
  }: IHandleGetCategoriesParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      const query = new URLSearchParams({
        page: String(page),
        perPage: String(perPage),
      });

      const response = await AxiosCalls.Get<ICategoriesData>(
        `/category/getAllCategories?${query.toString()}`
      );

      const payload = response?.data;

      const categories: ICategoryView[] = (payload?.items ?? []).map((c) => ({
        id: c.id,
        title: c.title ?? "Untitled",
        description: c.description ?? null,
        imageUrl: c.imageUrl ?? null,
        slug: c.slug ?? undefined,
        productsCount: Array.isArray(c.products) ? c.products.length : 0,
      }));

      setItems(categories);

      const meta = payload?.meta;
      if (typeof meta?.total === "number") setTotal?.(meta.total);
      if (typeof meta?.page === "number") setPage?.(meta.page);
      if (typeof meta?.perPage === "number") setPerPage?.(meta.perPage);
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      setItems([]);
      setTotal?.(0);
    } finally {
      setLoading(false);
    }
  }

  async handleGetAllAdminCategories({
    page,
    perPage,
    token,
    setLoading,
    setApiError,
    setItems,
    setTotal,
    setPage,
    setPerPage,
    onAuthError,
    onForbidden,
  }: IHandleGetAdminCategoriesParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      const query = new URLSearchParams({
        page: String(page),
        perPage: String(perPage),
      });

      const response = await AxiosCalls.Get<ICategoriesData>(
        `/category/getAllCategoriesAdmin?${query.toString()}`,
        token
      );

      const payload = response?.data;

      const categories: ICategoryView[] = (payload?.items ?? []).map((c) => ({
        id: c.id,
        title: c.title ?? "Untitled",
        description: c.description ?? null,
        imageUrl: c.imageUrl ?? null,
        slug: c.slug ?? undefined,
        productsCount: Array.isArray(c.products) ? c.products.length : 0,
      }));

      setItems(categories);

      const meta = payload?.meta;
      if (typeof meta?.total === "number") setTotal?.(meta.total);
      if (typeof meta?.page === "number") setPage?.(meta.page);
      if (typeof meta?.perPage === "number") setPerPage?.(meta.perPage);
    } catch (err) {
      setApiError(handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      }));
      setItems([]);
      setTotal?.(0);
    } finally {
      setLoading(false);
    }
  }

  async handleCreateCategory({
    data,
    token,
    setLoading,
    setApiError,
    setSuccessMsg,
    reset,
    onAuthError,
    onForbidden,
  }: IHandleCreateCategoryParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      await AxiosCalls.Post<
        void,
        {
          title: string;
          description: string;
          image: FileList;
        }
      >(
        "/category/add-category",
        {
          title: (data.title ?? "").trim(),
          description: (data.description ?? "").trim(),
          image: data.image,
        },
        token
      );

      setSuccessMsg("Category created successfully.");
      reset();
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }

  async handleGetCategoriesWithProducts({
    page,
    perPage,
    setLoading,
    setApiError,
    setItems,
    setMeta,
  }: IHandleGetCategoriesWithProductsParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      const queryString = new URLSearchParams({
        page: String(page),
        perPage: String(perPage),
      });

      const response = await AxiosCalls.Get<CategoriesResponse>(
        `/category/getAllCategories?${queryString.toString()}`
      );

      const data = response?.data ?? {};
      const categories: ICategoryData[] = Array.isArray(data?.items)
        ? data.items
        : [];

      setItems(categories);
      setMeta?.(data?.meta ?? {});
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      setItems([]);
      setMeta?.({ total: 0, page, perPage, pages: 0 });
    } finally {
      setLoading(false);
    }
  }

  getTargetPage(
    meta: CategoriesMeta,
    direction: "prev" | "next"
  ): number | null {
    if (!meta || meta.pages <= 1) return null;

    const delta = direction === "prev" ? -1 : 1;
    const target = meta.page + delta;

    if (target < 1 || target > meta.pages) return null;
    return target;
  }

  goToPage(options: {
    targetPage: number | null;
    meta: CategoriesMeta;
    loading: boolean;
    searchParams: URLSearchParams;
    perPage: number;
    setSearchParams: (next: URLSearchParams) => void;
  }): void {
    const {
      targetPage,
      meta,
      loading,
      searchParams,
      perPage,
      setSearchParams,
    } = options;

    if (loading) return;
    if (targetPage == null) return;
    if (targetPage < 1 || targetPage > meta.pages) return;
    if (targetPage === meta.page) return;

    const next = new URLSearchParams(searchParams);
    next.set("page", String(targetPage));
    next.set("perPage", String(perPage));
    setSearchParams(next);
  }

  async handleGetCategoryBySlug({
    slug,
    page,
    perPage,
    setLoading,
    setApiError,
    setCategory,
    setProducts,
    setMeta,
  }: IHandleGetCategoryBySlugParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      if (!slug) return;

      const query = new URLSearchParams({
        page: String(page),
        perPage: String(perPage),
      });

      const response = await AxiosCalls.Get<GetCategoryBySlugResponse>(
        `/category/getCategoryBySlug/${encodeURIComponent(
          slug
        )}?${query.toString()}`
      );

      const payload = response?.data;

      setCategory(payload.category);
      setProducts(payload.category.products.items);
      setMeta(payload.meta);
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      setCategory(null);
      setProducts([]);
      setMeta({ total: 0, page, perPage, pages: 0 });
    } finally {
      setLoading(false);
    }
  }

  async handleGetCategoryBySlugAdmin({
    slug,
    page,
    perPage,
    token,
    setLoading,
    setApiError,
    setCategory,
    setProducts,
    setMeta,
    onForbidden,
    onAuthError
  }: IHandleGetCategoryBySlugAdminParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      if (!slug) return;

      const query = new URLSearchParams({
        page: String(page),
        perPage: String(perPage),
      });

      const response = await AxiosCalls.Get<GetCategoryBySlugResponse>(
        `/category/getCategoryBySlugAdmin/${encodeURIComponent(
          slug
        )}?${query.toString()}`,
        token
      );

      const payload = response?.data;

      setCategory(payload.category);
      setProducts(payload.category.products.items);
      setMeta(payload.meta);
    } catch (err) {
      setApiError(handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      }));
      setCategory(null);
      setProducts([]);
      setMeta({ total: 0, page, perPage, pages: 0 });
    } finally {
      setLoading(false);
    }
  }

  async handleDeleteCategory({
    id,
    token,
    setLoading,
    setApiError,
    onAuthError,
    onForbidden,
    onSuccess,
  }: IHandleDeleteCategoryParams): Promise<void> {
    if (!id) return;

    setLoading(true);
    setApiError(null);

    try {
      await AxiosCalls.Delete<void>(
        `/category/delete-category/${encodeURIComponent(id)}`,
        token
      );

      onSuccess?.();
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }

  prepareEditCategoryFormState(args: {
    category: LoadedCategory | null;
    defaultValues: any;
    buildImageUrl: (path: string) => string | null;
  }) {
    const { category, defaultValues, buildImageUrl } = args;

    const formValues = {
      ...defaultValues,
      title: category?.title ?? "",
      description: category?.description ?? "",
      image: null as unknown as FileList,
    };

    const existingImages: ExistingUploadFile[] = category?.imageUrl
      ? (() => {
          const url = buildImageUrl(category.imageUrl);
          const name =
            String(category.imageUrl).split("/").pop() ?? "Current image";

          return [
            {
              id: String(category.imageUrl),
              name,
              url: url ?? undefined,
              type: "image/png",
            } as ExistingUploadFile,
          ];
        })()
      : [];

    return { category, formValues, existingImages };
  }

  handleInvalidEditCategoryForm({
    setApiError,
    setSuccessMsg,
  }: IHandleInvalidEditCategoryFormParams): void {
    setSuccessMsg(null);
    setApiError("Please fix the errors and try again.");
  }

  handleCancelEditCategoryForm({
    navigate,
  }: IHandleCancelEditCategoryFormParams): void {
    navigate(-1);
  }

  resetEditCategoryForm(params: IResetEditCategoryFormParams): void {
    const {
      didSave,
      loadedCategory,
      defaultValues,
      buildImageUrl,
      reset,
      setExistingImageFiles,
      setIsExistingImageRemoved,
      clearMessages,
    } = params;
    clearMessages();

    if (didSave) {
      setIsExistingImageRemoved(false);
      return;
    }
    if (!loadedCategory) return;

    const state = this.prepareEditCategoryFormState({
      category: loadedCategory,
      defaultValues,
      buildImageUrl,
    });

    reset(state.formValues);
    setIsExistingImageRemoved(false);
    setExistingImageFiles(state.existingImages);
  }

  async handleEditCategory({
    id,
    data,
    token,
    setLoading,
    setApiError,
    setSuccessMsg,
    onAuthError,
    onForbidden,
    isExistingImageRemoved,
  }: IHandleEditCategoryParams): Promise<void> {
    if (!id) {
      setApiError("Missing category id.");
      return;
    }

    setLoading(true);
    setApiError(null);
    const nextImage: FileList | null | undefined =
      data.image && data.image.length > 0
        ? data.image
        : isExistingImageRemoved
        ? null
        : undefined;

    try {
      await AxiosCalls.Put<
        void,
        {
          title: string;
          description: string;
          image?: FileList | null;
        }
      >(
        `/category/update-category/${encodeURIComponent(id)}`,
        {
          title: (data.title ?? "").trim(),
          description: (data.description ?? "").trim(),
          image: nextImage,
        },
        token
      );

      setSuccessMsg("Category updated successfully.");
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }

  filterCategoriesBySearch(
    categories: ICategoryView[],
    searchQuery: string
  ): ICategoryView[] {
    const querySearch = (searchQuery ?? "").trim().toLowerCase();
    if (!querySearch) return categories;

    return categories.filter((c) => {
      const title = String(c.title ?? "").toLowerCase();
      const desc = String(c.description ?? "").toLowerCase();
      const slug = String(c.slug ?? "").toLowerCase();

      return (
        title.includes(querySearch) ||
        desc.includes(querySearch) ||
        slug.includes(querySearch)
      );
    });
  }
}
